/**
 * Chuẩn tài chính quốc tế: dấu chấm (.) = thập phân, dấu phẩy (,) = hàng nghìn.
 *
 * Ngưỡng hiển thị:
 *   >= 0.01 tỷ (10 triệu VNĐ) → "X.XX tỷ"   — tối thiểu 2 chữ số thập phân
 *   <  0.01 tỷ (10 triệu VNĐ) → "X.X tr"    — triệu, 1 chữ số thập phân
 *
 * Ví dụ:
 *   82,300,000,000  →  "82.30 tỷ"
 *    6,500,000,000  →  "6.50 tỷ"
 *      500,000,000  →  "0.50 tỷ"
 *       10,000,000  →  "0.01 tỷ"
 *        5,000,000  →  "5.0 tr"
 *          150,000  →  "0.2 tr"
 */
export function formatVND(amount: number): string {
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';

  if (abs >= 10_000_000) {
    // >= 0.01 tỷ — hiển thị đơn vị tỷ, 2 chữ số thập phân
    const bil = abs / 1_000_000_000;
    const formatted = bil.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${sign}${formatted} tỷ`;
  }

  // < 10 triệu — hiển thị triệu, 1 chữ số thập phân
  const mil = abs / 1_000_000;
  if (mil >= 0.01) {
    const formatted = mil.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    return `${sign}${formatted} tr`;
  }

  // Rất nhỏ (< 10,000 VNĐ) — giữ nguyên đồng
  return `${sign}${Math.round(abs).toLocaleString('en-US')} đ`;
}

/**
 * Format gọn cho trục biểu đồ (Y-axis ticks) — không cần khoảng trắng, ký tự ngắn.
 * Ví dụ:  82,300,000,000 → "82.30t"   5,000,000 → "5.0tr"
 */
export function formatVNDAxis(amount: number): string {
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';

  if (abs >= 10_000_000) {
    const bil = abs / 1_000_000_000;
    const formatted = bil.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${sign}${formatted}t`;
  }

  const mil = abs / 1_000_000;
  if (mil >= 0.01) {
    const formatted = mil.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    return `${sign}${formatted}tr`;
  }

  return `${sign}${Math.round(abs).toLocaleString('en-US')}đ`;
}

export function formatNumber(n: number, decimals = 0): string {
  return n.toLocaleString('vi-VN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export function formatPercent(n: number, decimals = 2): string {
  return `${n >= 0 ? '+' : ''}${n.toFixed(decimals)}%`;
}

export function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function formatMonthYear(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('vi-VN', { month: 'short', year: 'numeric' });
}

export function assetClassColor(cls: string): string {
  const map: Record<string, string> = {
    'BĐS': '#10b981',
    'Cổ phiếu': '#38bdf8',
    'Thu nhập cố định': '#fbbf24',
    'Tiền mặt': '#a78bfa',
    'ESOP': '#fb7185',
    'Khác': '#94a3b8',
  };
  return map[cls] || '#94a3b8';
}
